import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';
import {
  isMicrophoneUnavailable,
  MICROPHONE_PROBLEM_HELP,
  MicrophoneProblemHelp,
  MicrophoneRequiredDialogData,
  MicrophoneRequiredDialogResult,
} from 'src/app/models/microphone-availability.model';
import { MicrophoneAvailabilityService } from 'src/app/services/communications/microphone-availability.service';

/**
 * The required-microphone alert: what is wrong, and the steps that fix it.
 *
 * Cannot be dismissed by a stray click or Escape — the care manager has to acknowledge it — but does
 * not lock them out of the app either: "Not now" closes it and the Call button stays disabled
 * until the microphone is back. Closes itself the moment the microphone becomes available, because
 * the usual fix (allowing the site, plugging the headset in) happens outside this page.
 */
@Component({
  selector: 'app-microphone-required-dialog',
  templateUrl: './microphone-required-dialog.component.html',
  styleUrls: ['./microphone-required-dialog.component.scss'],
})
export class MicrophoneRequiredDialogComponent implements OnInit, OnDestroy {
  readonly help: MicrophoneProblemHelp;
  checking = false;
  /** Set after a "Try again" that found the microphone still unavailable. */
  stillUnavailable = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<MicrophoneRequiredDialogComponent, MicrophoneRequiredDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: MicrophoneRequiredDialogData,
    private microphoneAvailability: MicrophoneAvailabilityService
  ) {
    this.dialogRef.disableClose = true;
    this.help = MICROPHONE_PROBLEM_HELP[data.problem];
  }

  /**
   * Whether the browser can tell this problem is fixed. A blocked permission or a missing device it
   * reports on its own; a silent, muted or loopback input only opening the microphone reveals, which
   * is what pressing Call does.
   */
  get closesOnAvailability(): boolean {
    return this.data.problem === 'permission-denied' || this.data.problem === 'no-device';
  }

  ngOnInit(): void {
    if (!this.closesOnAvailability) {
      return;
    }
    this.microphoneAvailability.availability$
      .pipe(
        // Only a change after opening: availability replays its current value on subscribe, and a
        // stale `available` must not close an alert the check has just raised.
        skip(1),
        filter((availability) => availability === 'available'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.dialogRef.close({ resolved: true }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async tryAgain(): Promise<void> {
    if (!this.closesOnAvailability) {
      // Nothing to re-read here: the next Call re-checks the microphone it is about to send.
      this.dialogRef.close({ resolved: false });
      return;
    }
    this.checking = true;
    this.stillUnavailable = false;
    const availability = await this.microphoneAvailability.recheck();
    this.checking = false;
    if (!isMicrophoneUnavailable(availability)) {
      // Closed here rather than left to the subscription: if the state was already `available`,
      // refreshing emits nothing new and the dialog would stay open over a fixed microphone.
      this.dialogRef.close({ resolved: true });
      return;
    }
    this.stillUnavailable = true;
  }

  notNow(): void {
    this.dialogRef.close({ resolved: false });
  }
}
